import React, { useState, useMemo, useEffect } from 'react';
import { useDogs } from '../hooks/useDogs';
import { APPOINTMENT_STATUS, STATUS_LABELS } from '../utils/vetUtils';
import '../styles/components/vet-calendar.css';

const STATUS_ORDER = [
    APPOINTMENT_STATUS.SCHEDULED,
    APPOINTMENT_STATUS.IN_PROGRESS,
    APPOINTMENT_STATUS.COMPLETED,
    APPOINTMENT_STATUS.CANCELLED,
];

const REASONS = [
    'Revisión anual', 'Vacuna', 'Esquema cachorro', 'Esquema adulto', 'Piel / pelaje', 'Digestivo',
    'Desparasitación', 'Emergencia', 'Seguimiento post-cirugía', 'Otro',
];

const VACCINE_TYPES = [
    'Parvovirus', 'Moquillo (Distemper)', 'Adenovirus (Hepatitis)',
    'Parainfluenza', 'Leptospirosis', 'Rabia', 'Bordetella', 'Corona',
];

const ADULT_SCHEMA_VACCINES = ['Rabia', 'Séxtuple', 'Bordetella', 'Giardia'];

const PUPPY_SCHEMA_STEPS = [
    { step: 1, offsetDays: 0,  label: 'Quíntuple + Desparasitación' },
    { step: 2, offsetDays: 15, label: 'Quíntuple + Desparasitación' },
    { step: 3, offsetDays: 30, label: 'Quíntuple + Desparasitación' },
    { step: 4, offsetDays: 45, label: 'Rabia' },
    { step: 5, offsetDays: 60, label: 'Bordetella + Giardia' },
];

const ALL_SLOTS = [
    '09:00','09:30','10:00','10:30','11:00','11:30',
    '12:00','12:30','14:00','14:30','15:00','15:30','16:00','16:30',
];

const DOW  = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const MES  = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
const MES_AB = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

const ymd      = d => `${d.getFullYear()}-${String(d.getMonth()).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const sameDay  = (a, b) => a && b && ymd(a) === ymd(b);
const addDays  = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isPast   = (d, today) => ymd(d) < ymd(today) && !sameDay(d, today);
const isSunday = d => d.getDay() === 0;
const startOfWeek = d => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

/* ---------- Calendar sub-component ---------- */
function Calendar({ selected, onSelect, appointments }) {
    const today = useMemo(() => new Date(), []);
    const [view, setView] = useState('mes');
    const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [weekStart, setWeekStart] = useState(startOfWeek(today));

    // Count scheduled appointments per day for availability display
    const countOnDay = useMemo(() => {
        const map = {};
        (appointments || []).forEach(a => {
            if (a.status === 'cancelled') return;
            const k = ymd(new Date(a.appointmentDate));
            map[k] = (map[k] || 0) + 1;
        });
        return map;
    }, [appointments]);

    const availFor = d => {
        if (isSunday(d)) return { closed: true, n: 0 };
        const n = countOnDay[ymd(d)] || 0;
        return { closed: false, n };
    };

    const availText = d => {
        const { closed, n } = availFor(d);
        if (closed) return 'Cerrado';
        if (n >= 10) return 'Lleno';
        if (n === 0) return 'Disponible';
        return `${n} agendadas`;
    };

    const monthCells = useMemo(() => {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const lead  = (first.getDay() + 6) % 7;
        const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < lead; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [cursor]);

    const weekCells = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

    const prevMonthDisabled = cursor.getFullYear() === today.getFullYear() && cursor.getMonth() <= today.getMonth();
    const prevWeekDisabled  = ymd(addDays(weekStart, 6)) < ymd(today);

    const goToday = () => {
        setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
        setWeekStart(startOfWeek(today));
    };

    const dayClass = d => {
        if (!d) return 'vc-day vc-day--out';
        if (isPast(d, today)) return 'vc-day vc-day--dis';
        const { closed, n } = availFor(d);
        let cls = 'vc-day';
        if (sameDay(d, selected)) cls += ' vc-day--sel';
        else if (sameDay(d, today)) cls += ' vc-day--today';
        if (closed) cls += ' vc-day--dis';
        else if (n >= 10) cls += ' vc-day--full';
        else if (n >= 6)  cls += ' vc-day--lo';
        return cls;
    };

    return (
        <div className="vc-cal vc-sec">
            {/* Head: view toggle + nav */}
            <div className="vc-cal-head">
                <div className="vc-seg">
                    <button className={view === 'semana' ? 'on' : ''} onClick={() => setView('semana')}>Semana</button>
                    <button className={view === 'mes' ? 'on' : ''} onClick={() => setView('mes')}>Mes</button>
                </div>
                <div className="vc-cal-nav">
                    <button className="vc-cal-today" onClick={goToday}>Hoy</button>
                    {view === 'mes' ? (
                        <>
                            <button className="vc-cal-arrow" disabled={prevMonthDisabled}
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
                            <span className="vc-cal-month">{MES[cursor.getMonth()]} {cursor.getFullYear()}</span>
                            <button className="vc-cal-arrow"
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
                        </>
                    ) : (
                        <>
                            <button className="vc-cal-arrow" disabled={prevWeekDisabled}
                                onClick={() => setWeekStart(addDays(weekStart, -7))}>‹</button>
                            <span className="vc-cal-month">{weekCells[0].getDate()}–{weekCells[6].getDate()} {MES_AB[weekCells[6].getMonth()]}</span>
                            <button className="vc-cal-arrow"
                                onClick={() => setWeekStart(addDays(weekStart, 7))}>›</button>
                        </>
                    )}
                </div>
            </div>

            {/* Month view */}
            {view === 'mes' && (
                <>
                    <div className="vc-dow-row">
                        {DOW.map(d => <div key={d} className="vc-dow">{d}</div>)}
                    </div>
                    <div className="vc-month">
                        {monthCells.map((d, i) => {
                            const dis = !d || isPast(d, today) || (d && isSunday(d)) || (d && availFor(d).n >= 10);
                            return (
                                <button key={i} className={dayClass(d)} disabled={dis} onClick={() => d && onSelect(d)}>
                                    {d && <>
                                        <span className="vc-dnum">{d.getDate()}</span>
                                        {!isPast(d, today) && !isSunday(d) && <span className="vc-ddot" />}
                                        {!isPast(d, today) && !isSunday(d) && <span className="vc-davail">{availText(d)}</span>}
                                    </>}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Week view */}
            {view === 'semana' && (
                <div className="vc-week">
                    {weekCells.map((d, i) => {
                        const dis = isPast(d, today) || isSunday(d);
                        return (
                            <button
                                key={i}
                                className={`vc-wday${sameDay(d, selected) ? ' vc-wday--sel' : ''}${dis ? ' vc-wday--dis' : ''}`}
                                disabled={dis}
                                onClick={() => onSelect(d)}
                            >
                                <div className="vc-wd">{DOW[i]}</div>
                                <div className="vc-wn">{d.getDate()}</div>
                                <div className="vc-wm">{MES_AB[d.getMonth()]}</div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Legend */}
            <div className="vc-legend">
                <span><i style={{ background: 'var(--vc-green)' }} />Disponible</span>
                <span><i style={{ background: 'var(--vc-warn)' }} />Pocos horarios</span>
                <span><i style={{ background: 'var(--vc-red)' }} />Lleno</span>
                <span><i style={{ background: 'var(--vc-muted-2)' }} />Cerrado</span>
            </div>
        </div>
    );
}

/* ---------- Main wizard ---------- */
const VetBookingWizard = ({ editingAppointment = null, onSubmit, onCancel, loading, appointments = [] }) => {
    const { dogs, loading: dogsLoading, loadDogs } = useDogs();
    const today = useMemo(() => new Date(), []);

    const [dogSearch, setDogSearch]     = useState('');

    // Search dogs against the full dataset (not just the first page) as the user types
    useEffect(() => {
        const timer = setTimeout(() => {
            loadDogs({ search: dogSearch, limit: 50 });
        }, 300);
        return () => clearTimeout(timer);
    }, [dogSearch, loadDogs]);
    const [pickedDog, setPickedDog]     = useState(editingAppointment?.dog || null);
    const [selectedDay, setSelectedDay] = useState(() => {
        if (editingAppointment?.appointmentDate) return new Date(editingAppointment.appointmentDate);
        return today;
    });
    const [selectedTime, setSelectedTime] = useState(() => {
        if (!editingAppointment?.appointmentDate) return '';
        const d = new Date(editingAppointment.appointmentDate);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    });
    const [cause,           setCause]           = useState(editingAppointment?.cause || '');
    const [selectedVaccines, setSelectedVaccines] = useState(editingAppointment?.vaccines || []);
    const [notes,  setNotes]  = useState(editingAppointment?.vetNotes || '');
    const [status, setStatus] = useState(editingAppointment?.status || APPOINTMENT_STATUS.SCHEDULED);
    const [errors, setErrors] = useState({});

    // Reset time when day changes
    useEffect(() => { setSelectedTime(''); }, [selectedDay]);

    // Slots taken by other appointments on the selected day
    const takenSlots = useMemo(() => {
        if (!selectedDay) return [];
        return appointments
            .filter(a => {
                if (editingAppointment && a._id === editingAppointment._id) return false;
                return sameDay(new Date(a.appointmentDate), selectedDay);
            })
            .map(a => {
                const d = new Date(a.appointmentDate);
                return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            });
    }, [appointments, selectedDay, editingAppointment]);

    const validate = () => {
        const errs = {};
        if (!pickedDog)   errs.dog  = 'Selecciona un perro';
        if (!selectedTime) errs.time = 'Elige un horario';
        if (!cause)       errs.cause = 'Selecciona un motivo';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const avoidSunday = d => { if (d.getDay() === 0) d.setDate(d.getDate() + 1); return d; };

    const schemaPreview = useMemo(() => {
        if (!selectedDay) return null;
        if (cause === 'Esquema cachorro') {
            return PUPPY_SCHEMA_STEPS.map(s => {
                const d = new Date(selectedDay);
                d.setDate(d.getDate() + s.offsetDays);
                avoidSunday(d);
                return { step: s.step, total: 5, label: s.label, date: d };
            });
        }
        if (cause === 'Esquema adulto') {
            const d1 = avoidSunday(new Date(selectedDay));
            const d2 = new Date(selectedDay);
            d2.setFullYear(d2.getFullYear() + 1);
            avoidSunday(d2);
            const vacLabel = selectedVaccines.length > 0 ? selectedVaccines.join(', ') : 'Selecciona las vacunas abajo';
            return [
                { step: 1, total: 2, label: vacLabel, date: d1 },
                { step: 2, total: 2, label: vacLabel, date: d2 },
            ];
        }
        return null;
    }, [cause, selectedDay, selectedVaccines]);

    const handleConfirm = () => {
        if (!validate()) return;
        const [h, m] = selectedTime.split(':').map(Number);
        const apptDate = new Date(selectedDay);
        apptDate.setHours(h, m, 0, 0);

        if (cause === 'Esquema cachorro') {
            onSubmit({ isSchema: true, dogId: pickedDog._id, firstDate: apptDate.toISOString() });
            return;
        }
        if (cause === 'Esquema adulto') {
            onSubmit({ isAdultSchema: true, dogId: pickedDog._id, firstDate: apptDate.toISOString(), vaccines: selectedVaccines });
            return;
        }

        onSubmit({
            dogId: pickedDog._id,
            appointmentDate: apptDate.toISOString(),
            cause,
            vaccines: cause === 'Vacuna' ? selectedVaccines : [],
            vetNotes: notes,
            status,
        });
    };

    const longDay = selectedDay
        ? `${DOW[(selectedDay.getDay() + 6) % 7]} ${selectedDay.getDate()} de ${MES[selectedDay.getMonth()]}`
        : '';

    return (
        <div className="vc-wrap">
            {/* Topbar */}
            <div className="vc-topbar">
                <button className="vc-back" type="button" onClick={onCancel}>‹</button>
                <span className="vc-title">{editingAppointment ? 'Editar cita' : 'Nueva cita'}</span>
                <div style={{ width: 42 }} />
            </div>

            {/* Dog picker */}
            <div className="vc-sec">
                <p className="vc-lbl">Para el perro</p>
                <input
                    className="vc-dog-search"
                    type="text"
                    placeholder="Buscar perro…"
                    value={dogSearch}
                    onChange={e => setDogSearch(e.target.value)}
                />
                {errors.dog && <p className="vc-error">{errors.dog}</p>}
                <div className="vc-dog-rail">
                    {dogsLoading && <span style={{ color: 'var(--vc-muted)', fontSize: '.88rem', padding: '8px 0' }}>Cargando…</span>}
                    {!dogsLoading && dogs.length === 0 && (
                        <span style={{ color: 'var(--vc-muted)', fontSize: '.88rem' }}>Sin resultados</span>
                    )}
                    {!dogsLoading && dogs.map(dog => (
                        <button
                            key={dog._id}
                            type="button"
                            className={`vc-dog-btn${pickedDog?._id === dog._id ? ' on' : ''}`}
                            onClick={() => { setPickedDog(dog); setErrors(p => ({ ...p, dog: '' })); }}
                        >
                            <div className="vc-dog-av">
                                {dog.pictureUrl || dog.picture
                                    ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                    : <span>{pickedDog?._id === dog._id ? '🐶' : '🐾'}</span>
                                }
                            </div>
                            <span className="vc-dog-name">{dog.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Calendar */}
            <div className="vc-sec">
                <p className="vc-lbl">Elige un día</p>
                <Calendar selected={selectedDay} onSelect={setSelectedDay} appointments={appointments} />
                {selectedDay && (
                    <div className="vc-selday">
                        📅 <span>Día seleccionado:</span> <b>{longDay}</b>
                    </div>
                )}
            </div>

            {/* Time slots */}
            <div className="vc-sec">
                <p className="vc-lbl">Hora {selectedDay && `· ${longDay}`}</p>
                {errors.time && <p className="vc-error">{errors.time}</p>}
                <div className="vc-times">
                    {ALL_SLOTS.map(s => {
                        const taken = takenSlots.includes(s);
                        return (
                            <button
                                key={s}
                                type="button"
                                className={`vc-slot${selectedTime === s ? ' vc-slot--sel' : ''}${taken ? ' vc-slot--dis' : ''}`}
                                disabled={taken}
                                onClick={() => { setSelectedTime(s); setErrors(p => ({ ...p, time: '' })); }}
                            >
                                {s}
                            </button>
                        );
                    })}
                    <input
                        type="time"
                        className={`vc-slot vc-slot--custom${selectedTime && !ALL_SLOTS.includes(selectedTime) ? ' vc-slot--sel' : ''}`}
                        value={selectedTime && !ALL_SLOTS.includes(selectedTime) ? selectedTime : ''}
                        onChange={e => {
                            if (e.target.value) {
                                setSelectedTime(e.target.value);
                                setErrors(p => ({ ...p, time: '' }));
                            }
                        }}
                    />
                </div>
            </div>

            {/* Status — edit mode only */}
            {editingAppointment && (
                <div className="vc-field vc-sec">
                    <p className="vc-lbl">Estado</p>
                    <div className="vc-sc-row">
                        {STATUS_ORDER.map(s => (
                            <button
                                key={s}
                                type="button"
                                className={`vc-sc vc-sc-${s}${status === s ? ' on' : ''}`}
                                onClick={() => setStatus(s)}
                            >
                                {STATUS_LABELS[s]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Motivo */}
            <div className="vc-sec">
                <p className="vc-lbl">Motivo</p>
                {errors.cause && <p className="vc-error">{errors.cause}</p>}
                <div className="vc-chips">
                    {REASONS.map(r => (
                        <button
                            key={r}
                            type="button"
                            className={`vc-chip${cause === r ? ' on' : ''}`}
                            onClick={() => {
                                setCause(r);
                                if (r !== 'Vacuna') setSelectedVaccines([]);
                                setErrors(p => ({ ...p, cause: '' }));
                            }}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            {/* Schema preview — shown for both schemas */}
            {(cause === 'Esquema cachorro' || cause === 'Esquema adulto') && schemaPreview && (
                <div className="vc-sec vc-field">
                    <p className="vc-lbl">📋 Citas que se crearán</p>
                    <div className="vc-schema-preview">
                        {schemaPreview.map(s => (
                            <div key={s.step} className="vc-schema-row">
                                <span className="vc-schema-step">Paso {s.step}/{s.total}</span>
                                <span className="vc-schema-label">{s.label}</span>
                                <span className="vc-schema-date">
                                    {DOW[(s.date.getDay() + 6) % 7]} {s.date.getDate()} {MES[s.date.getMonth()]}
                                    {selectedTime && ` · ${selectedTime}`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Vaccine types — shown for Vacuna or Esquema adulto */}
            {(cause === 'Vacuna' || cause === 'Esquema adulto') && (() => {
                const list = cause === 'Esquema adulto' ? ADULT_SCHEMA_VACCINES : VACCINE_TYPES;
                return (
                    <div className="vc-sec vc-field">
                        <p className="vc-lbl">💉 {cause === 'Esquema adulto' ? 'Vacunas aplicadas' : 'Tipo de vacuna'}</p>
                        <div className="vc-vaccine-grid">
                            {list.map(v => {
                                const checked = selectedVaccines.includes(v);
                                return (
                                    <label key={v} className={`vc-vaccine-item${checked ? ' on' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() =>
                                                setSelectedVaccines(prev =>
                                                    checked ? prev.filter(x => x !== v) : [...prev, v]
                                                )
                                            }
                                        />
                                        {v}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {/* Notes — hidden for schemas */}
            {cause !== 'Esquema cachorro' && cause !== 'Esquema adulto' && (
                <div className="vc-field vc-sec">
                    <p className="vc-lbl">Notas</p>
                    <textarea
                        rows={3}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Contexto para el veterinario…"
                    />
                </div>
            )}

            {/* Confirm */}
            <button
                type="button"
                className="vc-confirm vc-sec"
                onClick={handleConfirm}
                disabled={loading || dogsLoading}
            >
                {loading ? 'Guardando…' : editingAppointment ? 'Guardar cambios' : 'Confirmar cita'}
            </button>
        </div>
    );
};

export default VetBookingWizard;
